import { useState, useEffect, KeyboardEvent, useRef } from 'react';
import { Search, X, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

interface SelectItem {
  id: string;
  name: string;
}

interface SelectTagInputProps {
  value: string[];
  onChange: (_ids: string[]) => void;
  placeholder?: string;
  className?: string;
  buttonText?: string;
  showSearchIcon?: boolean;
  items: SelectItem[];
  loading?: boolean;
  onSearch?: (_query: string) => void;
  hideButton?: boolean;
}

export default function SelectTagInput({ 
  value = [], 
  onChange, 
  placeholder = 'Digite e aperte Enter',
  className = '',
  buttonText = 'OK',
  showSearchIcon = false,
  items = [],
  loading = false,
  onSearch,
  hideButton = false
}: SelectTagInputProps) {
  const [inputValue, setInputValue] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredItems, setFilteredItems] = useState<SelectItem[]>(items);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (inputValue.trim()) {
      const filtered = items.filter(item => 
        item.name.toLowerCase().includes(inputValue.toLowerCase())
      );
      setFilteredItems(filtered);
    } else {
      setFilteredItems(items);
    }
  }, [inputValue, items]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleAddTag = (item: SelectItem) => {
    if (!value.includes(item.id)) {
      const newTags = [...value, item.id];
      onChange(newTags);
      setInputValue('');
      setShowDropdown(false);
    }
  };

  const handleAddFromInput = () => {
    const trimmedValue = inputValue.trim();
    
    if (trimmedValue) {
      if (filteredItems.length === 1) {
        handleAddTag(filteredItems[0]);
      } else {
        const newTag = {
          id: trimmedValue,
          name: trimmedValue
        };
        handleAddTag(newTag);
      }
    }
  };

  const handleRemoveTag = (index: number) => {
    const newTags = value.filter((_, i) => i !== index);
    onChange(newTags);
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddFromInput();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setInputValue(query);
    
    if (query.trim()) {
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
    }
    
    onSearch?.(query);
  };

  const handleInputFocus = () => {
    if (filteredItems.length > 0) {
      setShowDropdown(true);
    }
  };

  const getSelectedItemNames = () => {
    return value.map(id => items.find(item => item.id === id)?.name || id);
  };

  return (
    <div className={`w-full ${className}`} ref={containerRef}>
      <div className="flex gap-2 w-full relative">
        <div className="flex-1 relative w-full">
          {showSearchIcon && (
            <Search className="absolute left-3 top-1/2 transform z-20 -translate-y-1/2 text-gray-400 w-4 h-4" />
          )}
          <Input
            value={inputValue}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            onFocus={handleInputFocus}
            placeholder={placeholder}
            className={`w-full ${showSearchIcon ? 'pl-10' : ''}`}
          />
          
          {showDropdown && (
            <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-md shadow-lg z-30 max-h-48 overflow-y-auto">
              {loading ? (
                <div className="flex items-center gap-2 px-3 py-3 text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Carregando...
                </div>
              ) : (
                filteredItems.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => handleAddTag(item)}
                    disabled={value.includes(item.id)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-100 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    {item.name}
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      
        {!hideButton && (
          <Button
            type="button"
            onClick={handleAddFromInput}
            disabled={loading || !inputValue.trim()}
            className="bg-gray-800 text-white hover:bg-gray-900 disabled:bg-gray-400"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : buttonText}
          </Button>
        )}
      </div>
      
      {!loading && value.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {getSelectedItemNames().map((name, index) => (
            <div key={`${name}-${index}`} className="flex items-center gap-2 bg-black text-white px-3 py-1 rounded-full text-sm">
              <span>{name}</span>
              <button
                type="button"
                onClick={() => handleRemoveTag(index)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-3 h-3 text-white" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
